
import ModalWrapper from '@Components/Modals/ModalWrapper/ModalWrapper.tsx';
import AssetServices from '@Services/Asset.services.ts';
import { ExcelAsset } from '@Types/Asset.types.ts';
import { EXCEL_ASSET_FIELD_NAMES, EXCEL_FILE_LOCATION, EXCEL_VALUE_MAP } from '@Utils/constants.tsx';
import { ReactNode, useEffect, useRef, useState } from 'react';
import './AssetUploadModal.scss';
import { Workbook } from 'exceljs';
import { saveAs } from 'file-saver';

type Props = {
  data: Partial<ExcelAsset>[] | null;
  isOpen: boolean;
  onClose: (...props: any) => void;
  onCreate: (...props: any) => void;
};

const AlwaysScrollToBottom = () => {
  const elementRef = useRef<null | HTMLDivElement>(null);
  useEffect(() => elementRef.current?.scrollIntoView());
  return <div ref={elementRef} />;
};

const AssetUploadModal = ({ data, isOpen, onClose, onCreate }: Props) => {
  const block = 'upload_modal';
  const textClass = `${block}__text`;
  const statusClass = `${block}__status`;
  const [progress, setProgress] = useState<string>('');
  const [statuses, setStatuses] = useState<{ [key: number]: ReactNode }>({});
  const [, setAllowClose] = useState<boolean>(false);
  const [errorRows, setErrorRows] = useState<{ value: any }[][]>([]);
  const [haveFailedAssets, setHaveFailedAssets] = useState<boolean>(false);

  const formatProgress = (msg: string, values: string[]) => {
    let result = msg;
    values.forEach((value, index) => {
      result = result.replace(`$${index + 1}`, value);
    });
    return result;
  };

  const formatErrorMessage = (errorMessage: string, fields: string[]) => {
    let message = errorMessage;

    const split = errorMessage.split(': ');
    if (split.length > 1) {
      message = `${split[0]}:`;
    }

    const mappedFields = fields
      .map((field) => {
        if (EXCEL_ASSET_FIELD_NAMES[field]) {
          return EXCEL_ASSET_FIELD_NAMES[field];
        }
        return field;
      })
      .join(', ');

    return `${message} ${mappedFields}`;
  };

  const reset = () => {
    setProgress('');
    setStatuses({});
    setAllowClose(false);
  };

  const processAsset = async (asset: any, index: number) => {
    const currentAsset = `Asset #${index + 1}`;
    setStatuses((prev) => ({
      ...prev,
      [index]: (
        <div className={statusClass}>
          <h3 className={`${statusClass} ${textClass}`}>
            <i className='fa-solid fa-hourglass-clock' /> {currentAsset}:
          </h3>
          <h4 className={textClass}> Processing...</h4>
        </div>
      ),
    }));

    await new Promise((r) => {
      setTimeout(r, 800);
    }); // Wait before sending next request
    try {
      const response = await AssetServices.uploadAsset(asset);

      onCreate();
      setStatuses((prev) => ({
        ...prev,
        [index]: (
          <div className={statusClass}>
            <h3 className={`${statusClass} ${textClass}`}>
              <i className={`fa-regular fa-circle-check ${block}__icon--green`} />
              {currentAsset}:
            </h3>
            <h4 className={textClass}>{response.data.message}</h4>
          </div>
        ),
      }));
      return true;
    } catch (err: any) {
      const errorRow = Array.from(EXCEL_VALUE_MAP.values()).map((field) => ({
        value: asset[field] ?? null,
      }));
      errorRow.push({
        value: formatErrorMessage(err.response.data.message, err.response.data.fields),
      });

      setErrorRows((errRows) => [...errRows, errorRow]);

      setStatuses((prev) => ({
        ...prev,
        [index]: (
          <div className={statusClass}>
            <h3 className={`${statusClass} ${textClass}`}>
              <i className={`fa-sharp fa-regular fa-circle-exclamation ${block}__icon--red`} />
              {currentAsset}:
            </h3>
            <h4 className={textClass}>{formatErrorMessage(err.response.data.message, err.response.data.fields)}</h4>
          </div>
        ),
      }));
      return false;
    }
  };

  useEffect(() => {
    if (!isOpen) {
      reset();
      return;
    }

    if (!data || data.length === 0) {
      onClose();
      return;
    }

    setAllowClose(false);

    const runQueue = async () => {
      let successful = 0;
      setHaveFailedAssets(false);
      setErrorRows([]);
      for (let index = 0; index < data.length; index++) {
        setProgress(formatProgress("Processing asset $1 of $2...", [(index + 1).toString(), data.length.toString()]));
        const asset = data[index];
        // Needed to process assets one by one
        const success = await processAsset(asset, index);
        if (success) {
          successful++;
        }
      }
      setHaveFailedAssets(successful < data.length);
      const currentProgress = formatProgress("Upload complete: $1 of $2 succeeded, $3 failed.", [
        successful.toString(),
        data.length.toString(),
        (data.length - successful).toString(),
      ]);

      setProgress(currentProgress);
      setAllowClose(true);
    };

    runQueue().catch((err) => console.error(err));
  }, [data, isOpen]);

  const createFailedAssetsExcel = () => {
    fetch(EXCEL_FILE_LOCATION)
      .then((response) => response.arrayBuffer())
      .then((arrayBuffer) => {
        const workbook = new Workbook();
        return workbook.xlsx.load(arrayBuffer);
      })
      .then((workbook) => {
        const worksheet = workbook.getWorksheet(1);
        const errorCell = worksheet?.getCell('V1');
        if (errorCell) {
          errorCell.value = 'Error';
          errorCell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: '00FF0000' },
          };
        }
        errorRows.forEach((rowArray, rowIndex) => {
          const row = worksheet?.getRow(rowIndex + 2);
          if (row) {
            rowArray.forEach((cellObj, colIndex) => {
              row.getCell(colIndex + 1).value = cellObj.value;
            });
            row.commit();
          }
        });
        return workbook.xlsx.writeBuffer();
      })
      .then((buffer) => {
        const blob = new Blob([buffer], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        saveAs(blob, 'error.xlsx');
      });
  };

  return (
    <div className='modal--fit-content'>
      <ModalWrapper
        handleClose={onClose}
        show={isOpen}
        title="Upload Assets"
        hideBottomElement={!haveFailedAssets}
        primaryButtonText="Download Failed Assets"
        primaryAction={() => createFailedAssetsExcel()}>
        <div className={`${block}__base`}>
          <h2>{progress}</h2>

          <div className={`${block}__content`}>
            {Object.entries(statuses).map((value, index) => (
              <div className={`${block}__container`} key={index}>
                {value[1]}
              </div>
            ))}
            <AlwaysScrollToBottom />
          </div>
        </div>
      </ModalWrapper>
    </div>
  );
};
export default AssetUploadModal;
